import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import ProductCard from './ProductCard';
import MinisiteView from './MinisiteView';
import { markdownToExcerpt } from '../utils/text';
import { resolveMediaUrl } from '../utils/media';
import { API_BASE } from '../utils/apiBase';
import { useLifeEvents } from '../utils/lifeEvents';
import Faq from './Faq';
import Seo from './Seo';
import { useT, useLang, useFormat, DEFAULT_LANG } from '../i18n';
import { useProductTexts } from '../utils/productI18n';
import {
  MINISITE_OCCASION_KEYS, MINISITE_HUB_PATH, MINISITE_VIDEO_URL, modulesOfOccasion,
  useMinisiteOccasions, useOccasionByKey, useMinisiteModules, useMinisiteSocle, useMinisiteArguments,
} from '../utils/minisiteOccasions';

const MODELES_PAR_ONGLET = 12;

// Filtres de style : les libellés sont dans minisite-catalog.styles.<clé>.
const STYLE_KEYS = ['tous', 'romantique', 'elegant'];

// Au milieu d'une phrase, un titre est repris en minuscules en français (« … — faire-part de mariage animé ») ;
// dans les autres langues on garde la casse de la traduction (l'allemand met une majuscule aux noms).
const useInline = () => {
  const { lang } = useLang();
  return (texte) => (lang === DEFAULT_LANG ? texte.toLowerCase() : texte);
};

// Produits « à type » (option_schema.product_type) : 'minisite' = les fiches « composition » des
// faire-part et invitations animés, 'galerie' = la galerie photos des invités. Ils n'apparaissent
// pas dans les catégories, qui ne listent que les produits ayant des visuels de carte à gratter.
export const useMinisiteProducts = () => {
  const [products, setProducts] = useState(null);
  useEffect(() => {
    axios.get(`${API_BASE}/api-2026.php`, {
      params: { cleApi: '34KVHFQSP226YTk4n256QP6rGbqhA5w2ymY7W3pLwdbuc3i75', service: 'getProducts' },
    })
      .then((response) => setProducts((response.data?.data || []).filter((p) => p.option_schema?.product_type)))
      .catch(() => setProducts([]));
  }, []);
  return products;
};

export const isMinisite = (product) => product.option_schema?.product_type === 'minisite';
export const occasionOf = (product) => product.option_schema?.defaults?.occasion || 'mariage';

// Encart vers la galerie photos des invités (produit à part), sur les pages où il a du sens.
export const GalerieInvitesTeaser = ({ products }) => {
  const t = useT('minisite-catalog');
  const tp = useProductTexts();
  const galerie = (products || []).find((p) => p.option_schema?.product_type === 'galerie');
  if (!galerie) return null;
  return (
    <Paper variant="outlined" sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 3, padding: 3, marginTop: 6 }}>
      <Box component="img" src={resolveMediaUrl(galerie.featured_image)} alt={tp(galerie).titre} loading="lazy" sx={{ width: 160, borderRadius: 2 }} />
      <Box sx={{ flex: 1, minWidth: 240 }}>
        <Typography variant="h5" sx={{ marginBottom: 1 }}>{t('minisite-catalog.galerie.title')}</Typography>
        <Typography variant="body2" sx={{ marginBottom: 2 }}>
          {t('minisite-catalog.galerie.body')}
        </Typography>
        <Button variant="contained" color="secondary" component={Link} to={`/produit/${galerie.url}`}>
          {t('minisite-catalog.galerie.cta', { amount: parseFloat(galerie.prix_ttc) })}
        </Button>
      </Box>
    </Paper>
  );
};

export const MinisiteProductCard = ({ product }) => {
  const t = useT('minisite-catalog');
  const tp = useProductTexts();
  const texts = tp(product); // titre et description courte traduits (repli : français)
  // Langue traduite : la description courte du modèle ; sinon (français, ou pas encore traduit) l'extrait de la description.
  const extrait = texts !== product && texts.description_courte
    ? texts.description_courte
    : markdownToExcerpt(product.description, 110);
  return (
    <ProductCard
      visuels={product.featured_image ? [{ url: product.featured_image }] : []}
      title={texts.titre}
      price={parseFloat(product.prix_ttc) || 0}
      linkTo={`/produit/${product.url}`}
    >
      <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
        {extrait}
      </Typography>
      <Button variant="contained" color="secondary" sx={{ marginTop: 2 }} component={Link} to={`/produit/${product.url}`}>
        {t('minisite-catalog.card.demo')}
      </Button>
    </ProductCard>
  );
};

export const MinisiteArguments = () => {
  const args = useMinisiteArguments();
  return (
    <Box component="ul" sx={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 1 }}>
      {args.map((argument) => (
        <Typography component="li" variant="body2" key={argument}>✓ {argument}</Typography>
      ))}
    </Box>
  );
};

// « Ce que contient… » : le socle commun à tous les modèles, puis les modules du produit
// (minisiteModules.json — la même liste règle ce que l'éditeur ouvre après l'achat).
// `compact` (fiche produit, où le détail figure déjà dans la description) : les intitulés seulement.
export const MinisiteContenu = ({ occasionKey, titre, compact = false }) => {
  const inclus = modulesOfOccasion(occasionKey);
  const occasion = useOccasionByKey(occasionKey);
  const modules = useMinisiteModules();
  const socle = useMinisiteSocle();
  return (
    <Paper variant="outlined" sx={{ padding: 2 }}>
      <Typography sx={{ fontWeight: 600, marginBottom: 1 }}>{titre || occasion.contenuTitre}</Typography>
      <Box component="ul" sx={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 0.75 }}>
        {modules.filter((m) => inclus.includes(m.key)).map((m) => (
          <Typography component="li" variant="body2" key={m.key}>✓ <strong>{m.label}</strong>{compact ? '' : ` — ${m.detail}`}</Typography>
        ))}
        {!compact && socle.map((ligne) => (
          <Typography component="li" variant="body2" color="text.secondary" key={ligne}>✓ {ligne}</Typography>
        ))}
      </Box>
    </Paper>
  );
};

// Tableau comparatif : une colonne par occasion (avec son prix), une ligne par module.
export const MinisiteComparatif = ({ products }) => {
  const t = useT('minisite-catalog');
  const fmt = useFormat();
  const occasions = useMinisiteOccasions();
  const modules = useMinisiteModules();
  const colonnes = MINISITE_OCCASION_KEYS
    .map((key) => ({ key, occasion: occasions[key], premier: (products || []).find((p) => isMinisite(p) && occasionOf(p) === key) }))
    .filter((c) => products === null || c.premier);
  const cell = { padding: '8px 10px', borderBottom: '1px solid rgba(0,0,0,0.08)', textAlign: 'center', fontSize: '0.85rem' };
  return (
    <Box sx={{ overflowX: 'auto', marginBottom: 5 }}>
      <Box component="table" sx={{ borderCollapse: 'collapse', margin: '0 auto', minWidth: 720 }}>
        <thead>
          <tr>
            <Box component="th" sx={{ ...cell, textAlign: 'left' }}>{t('minisite-catalog.comparatif.inclus')}</Box>
            {colonnes.map((c) => (
              <Box component="th" key={c.key} sx={{ ...cell, fontWeight: 600, lineHeight: 1.25 }}>
                <Link to={c.occasion.hub.path} style={{ color: 'inherit' }}>{c.occasion.label}</Link>
                {c.premier && <Box sx={{ fontWeight: 400, color: 'text.secondary' }}>{fmt.money(parseFloat(c.premier.prix_ttc))}</Box>}
              </Box>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <Box component="td" sx={{ ...cell, textAlign: 'left' }}>{t('minisite-catalog.comparatif.base')}</Box>
            {colonnes.map((c) => <Box component="td" key={c.key} sx={cell}>✓</Box>)}
          </tr>
          {modules.map((m) => (
            <tr key={m.key}>
              <Box component="td" sx={{ ...cell, textAlign: 'left' }} title={m.detail}>{m.label}</Box>
              {colonnes.map((c) => (
                <Box component="td" key={c.key} sx={{ ...cell, color: modulesOfOccasion(c.key).includes(m.key) ? 'inherit' : 'text.disabled' }}>
                  {modulesOfOccasion(c.key).includes(m.key) ? '✓' : '—'}
                </Box>
              ))}
            </tr>
          ))}
        </tbody>
      </Box>
    </Box>
  );
};

// Vidéo de démonstration de l'éditeur, la même partout ; rien tant qu'elle n'est pas fournie.
export const MinisiteVideo = () => {
  const t = useT('minisite-catalog');
  return MINISITE_VIDEO_URL ? (
    <Box sx={{ maxWidth: 760, margin: '0 auto 32px' }}>
      <Typography variant="h5" sx={{ textAlign: 'center', marginBottom: 1 }}>{t('minisite-catalog.video.title')}</Typography>
      <video src={MINISITE_VIDEO_URL} controls preload="metadata" style={{ width: '100%', borderRadius: 12 }} />
    </Box>
  ) : null;
};

// Une tuile par occasion (image du premier modèle, nombre de modèles, prix d'appel) : page chapeau et accueil.
export const MinisiteOccasionGrid = ({ products }) => {
  const t = useT('minisite-catalog');
  const occasions = useMinisiteOccasions();
  const parOccasion = (key) => (products || []).filter((p) => isMinisite(p) && occasionOf(p) === key);
  return (
    <Grid container spacing={3}>
      {MINISITE_OCCASION_KEYS.map((key) => {
        const o = occasions[key];
        const modeles = parOccasion(key);
        const premier = modeles[0];
        if (products !== null && modeles.length === 0) return null; // occasion sans modèle en base : pas de tuile vide
        return (
          <Grid item xs={6} sm={4} md={3} key={key}>
            <Paper variant="outlined" component={Link} to={o.hub.path} sx={{ display: 'block', textDecoration: 'none', color: 'inherit', overflow: 'hidden', height: '100%' }}>
              {premier?.featured_image && (
                <Box component="img" src={resolveMediaUrl(premier.featured_image)} alt={o.hub.titre} loading="lazy" sx={{ width: '100%', aspectRatio: '1', objectFit: 'cover', display: 'block' }} />
              )}
              <Box sx={{ padding: 1.5 }}>
                <Typography sx={{ fontWeight: 600, lineHeight: 1.25, marginBottom: 0.5 }}>{o.hub.titre}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {products === null && ' '}
                  {products !== null && (premier
                    ? t('minisite-catalog.grid.modelsFrom', { count: modeles.length, amount: parseFloat(premier.prix_ttc) })
                    : t('minisite-catalog.grid.models', { count: modeles.length }))}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        );
      })}
    </Grid>
  );
};

// Page d'une occasion (/faire-part-mariage-anime, /invitation-bapteme-animee…) : promesse, démo dans
// le téléphone, modèles. Sans `occasionKey` : page chapeau qui présente toutes les occasions.
const MinisiteCatalogPage = ({ occasionKey }) => {
  const t = useT('minisite-catalog', 'common');
  const inline = useInline();
  const occasions = useMinisiteOccasions();
  const evenements = useLifeEvents();
  const products = useMinisiteProducts();
  const [style, setStyle] = useState('tous');
  // Page chapeau : onglet ouvert (une occasion). `#naissance` dans l'adresse ouvre directement l'onglet.
  const [onglet, setOnglet] = useState(() => {
    const hash = window.location.hash.replace('#', '');
    return MINISITE_OCCASION_KEYS.includes(hash) ? hash : MINISITE_OCCASION_KEYS[0];
  });
  const occasion = occasionKey ? occasions[occasionKey] : null;
  const evenement = occasion ? evenements.find((e) => e.key === occasion.hub.evenement) : null;

  const visible = useMemo(
    () => (products || []).filter((p) => isMinisite(p) && (!occasionKey || occasionOf(p) === occasionKey)
      && (style === 'tous' || p.option_schema?.style === style)),
    [products, style, occasionKey],
  );

  if (!occasion) {
    return (
      <Box sx={{ padding: { xs: 1, sm: 3 } }}>
        <Seo
          titre={t('minisite-catalog.hub.seoTitle')}
          description={t('minisite-catalog.hub.seoDescription')}
          path={MINISITE_HUB_PATH}
        />
        <Typography variant="h1" sx={{ textAlign: 'center', marginBottom: 2 }}>{t('minisite-catalog.hub.title')}</Typography>
        <Typography sx={{ maxWidth: 760, margin: '0 auto 24px', textAlign: 'center' }}>
          {t('minisite-catalog.hub.intro')}
        </Typography>
        <Paper variant="outlined" sx={{ maxWidth: 520, margin: '0 auto 32px', padding: 2 }}><MinisiteArguments /></Paper>
        <MinisiteVideo />

        {(() => {
          const parOccasion = (key) => (products || []).filter((p) => isMinisite(p) && occasionOf(p) === key);
          const ouverts = MINISITE_OCCASION_KEYS.filter((key) => products === null || parOccasion(key).length > 0);
          const courant = ouverts.includes(onglet) ? onglet : ouverts[0];
          if (!courant) return null;
          const o = occasions[courant];
          const modeles = parOccasion(courant);
          return (
            <>
              <Tabs
                value={courant}
                onChange={(_, v) => { setOnglet(v); window.history.replaceState(null, '', `#${v}`); }}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                textColor="secondary"
                indicatorColor="secondary"
                sx={{ marginBottom: 3, borderBottom: '1px solid rgba(0,0,0,0.1)' }}
              >
                {ouverts.map((key) => (
                  <Tab
                    key={key}
                    value={key}
                    label={products ? t('minisite-catalog.hub.tab', { label: occasions[key].label, n: parOccasion(key).length }) : occasions[key].label}
                  />
                ))}
              </Tabs>

              <Grid container spacing={3} sx={{ marginBottom: 4 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h2" component="h2" sx={{ marginBottom: 1 }}>{o.hub.titre}</Typography>
                  {modeles[0] && (
                    <Typography sx={{ fontWeight: 600, marginBottom: 1 }}>{t('minisite-catalog.hub.priceLine', { amount: parseFloat(modeles[0].prix_ttc) })}</Typography>
                  )}
                  <Typography>{o.hub.texte}</Typography>
                </Grid>
                <Grid item xs={12} md={6}><MinisiteContenu occasionKey={courant} /></Grid>
              </Grid>

              <Grid container spacing={3}>
                {modeles.slice(0, MODELES_PAR_ONGLET).map((product) => (
                  <Grid item xs={12} sm={6} md={4} key={product.id}><MinisiteProductCard product={product} /></Grid>
                ))}
              </Grid>
              <Box sx={{ textAlign: 'center', margin: '24px 0 56px' }}>
                <Button variant="contained" color="secondary" component={Link} to={o.hub.path}>
                  {modeles.length > MODELES_PAR_ONGLET
                    ? t('minisite-catalog.hub.viewAll', { n: modeles.length, titre: inline(o.hub.titre) })
                    : t('minisite-catalog.hub.viewPage', { titre: inline(o.hub.titre) })}
                </Button>
              </Box>
            </>
          );
        })()}

        <Typography variant="h4" component="h2" sx={{ textAlign: 'center', marginBottom: 1 }}>{t('minisite-catalog.comparatif.title')}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', marginBottom: 2 }}>
          {t('minisite-catalog.comparatif.intro')}
        </Typography>
        <MinisiteComparatif products={products} />
        <GalerieInvitesTeaser products={products} />
        <Faq page="hub" />
      </Box>
    );
  }

  return (
    <Box sx={{ padding: { xs: 1, sm: 3 } }}>
      <Seo titre={occasion.hub.metaTitle} description={occasion.hub.metaDescription} path={occasion.hub.path} image={evenement?.image} />

      <Grid container spacing={4} alignItems="center" sx={{ marginBottom: 4 }}>
        <Grid item xs={12} md={7}>
          <Typography variant="h1" sx={{ marginBottom: 2 }}>{occasion.hub.titre}</Typography>
          <Typography sx={{ marginBottom: 2 }}>{occasion.hub.texte}</Typography>
          <MinisiteContenu occasionKey={occasionKey} />
          <Box sx={{ marginTop: 2 }}><MinisiteArguments /></Box>
          <Button component={Link} to={MINISITE_HUB_PATH} size="small" sx={{ marginTop: 2 }}>
            {t('minisite-catalog.occasion.seeAll')}
          </Button>
        </Grid>
        <Grid item xs={12} md={5}>
          <div className="ms-phone">
            <MinisiteView embedded params={{ occasion: occasionKey, ...occasion.demo, ...occasion.hub.demo }} />
          </div>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', marginTop: 1 }}>
            {t('minisite-catalog.occasion.demoCaption')}
          </Typography>
        </Grid>
      </Grid>

      <MinisiteVideo />

      <Typography variant="h4" sx={{ textAlign: 'center', marginBottom: 2 }}>{t('minisite-catalog.occasion.chooseModel')}</Typography>
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap', marginBottom: 4 }}>
        {STYLE_KEYS.map((key) => (
          <Chip
            key={key}
            label={t(`minisite-catalog.styles.${key}`)}
            clickable
            color={style === key ? 'secondary' : 'default'}
            variant={style === key ? 'filled' : 'outlined'}
            onClick={() => setStyle(key)}
          />
        ))}
      </Box>

      {products === null && <Typography sx={{ textAlign: 'center' }}>{t('common.loading')}</Typography>}
      {products !== null && visible.length === 0 && (
        <Typography sx={{ textAlign: 'center' }}>{t('minisite-catalog.occasion.none')}</Typography>
      )}
      <Grid container spacing={3}>
        {visible.map((product) => (
          <Grid item xs={12} sm={6} md={4} key={product.id}><MinisiteProductCard product={product} /></Grid>
        ))}
      </Grid>
      {occasionKey === 'mariage' && <GalerieInvitesTeaser products={products} />}

      <Box sx={{ maxWidth: 820, margin: '56px auto 0' }}>
        {(occasion.hub.seo || []).map((bloc, i) => (
          <Box key={i} sx={{ marginBottom: 4 }}>
            <Typography variant="h4" component="h2" sx={{ marginBottom: 1 }}>{bloc.titre}</Typography>
            <Typography>{bloc.texte}</Typography>
          </Box>
        ))}
        {evenement && (
          <Typography>
            {t.rich('minisite-catalog.occasion.seeAlso', { titre: inline(evenement.titre) }, { lien: <Link to={evenement.path} /> })}
          </Typography>
        )}
      </Box>
      <Faq page={`occ-${occasionKey}`} />
    </Box>
  );
};

export default MinisiteCatalogPage;
